/**
 * 결재선 정보를 API 형식으로 변환하는 유틸 함수
 * - 새로운 구조 (approvalLine 객체) → API 구조 (sign1-5, check1-4, agreement)
 * - PC 버전과 동일한 state 계산 로직 적용
 * @param {Object} approvalLine - 결재선 정보 객체
 * @param {number} defaultEnumber - 기본 사용자 번호 (로그인한 사용자)
 * @param {number|null} [docState=null] - 문서 상태 (6: 수정요청 재상신 시 백엔드가 수정요청자를 찾을 수 있도록 state=6 유지)
 * @param {number} [mode=0] - 저장 모드 (0: 새문서 상신, 1: 임시문서 상신, 2: 재상신). 2일 때는 기존 state 유지.
 * @returns {Object} API 형식의 결재선 데이터
 */
function convertApprovalLine(approvalLine = {}, defaultEnumber = 0, docState = null, mode = 0) {
  // 기안자 (draft1) → sign1 (로그인한 사용자)
  // draft1이 없거나 enumber가 0이면 defaultEnumber 사용
  const { draft1 } = approvalLine;
  const draft1Enumber = draft1?.enumber ? Number(draft1.enumber) : 0;
  const sign1Enumber = draft1Enumber > 0 ? draft1Enumber : defaultEnumber || 0;

  // 결재자 (approval1-4) → sign2-5
  const sign2Enumber = approvalLine.approval1?.enumber || 0;
  const sign3Enumber = approvalLine.approval2?.enumber || 0;
  const sign4Enumber = approvalLine.approval3?.enumber || 0;
  const sign5Enumber = approvalLine.approval4?.enumber || 0;

  // 검토자 (review1-4) → check1-4
  const check1Enumber = approvalLine.review1?.enumber || 0;
  const check2Enumber = approvalLine.review2?.enumber || 0;
  const check3Enumber = approvalLine.review3?.enumber || 0;
  const check4Enumber = approvalLine.review4?.enumber || 0;

  // 합의자 (agreement1) → agreement
  const agreementEnumber = approvalLine.agreement1?.enumber || 0;

  // ===== 전역 결재 순서(order) 처리 =====
  // 서버는 sign/check/agreement의 order를 "전역 순서"로 저장하고,
  // 현재 결재자의 order + 1에 해당하는 사람을 다음 결재자로 올립니다.
  // 따라서 프론트도 전역 순서(order)를 반드시 유지해야 합니다.

  const sign1Order = Number(approvalLine.draft1?.order) > 0 ? Number(approvalLine.draft1.order) : 1;

  function getSlotOrder(slot, fallback) {
    const value = Number(slot?.order);
    return value > 0 ? value : fallback;
  }

  // 전역 순서: 유효한 슬롯(sign2~5, check1~4, agreement)에만 2, 3, 4, ... 순차 부여.
  // 비어 있는 슬롯은 order=0. 검토자만 있을 때 첫 검토자가 order=2, state=2가 되어 미결함에 표시됨.
  // slot.order가 이미 있으면 우선 사용(과거 데이터·모달 저장 호환).
  let nextOrder = 2;
  const sign2Order = sign2Enumber ? getSlotOrder(approvalLine.approval1, nextOrder++) : 0;
  const sign3Order = sign3Enumber ? getSlotOrder(approvalLine.approval2, nextOrder++) : 0;
  const sign4Order = sign4Enumber ? getSlotOrder(approvalLine.approval3, nextOrder++) : 0;
  const sign5Order = sign5Enumber ? getSlotOrder(approvalLine.approval4, nextOrder++) : 0;

  const check1Order = check1Enumber ? getSlotOrder(approvalLine.review1, nextOrder++) : 0;
  const check2Order = check2Enumber ? getSlotOrder(approvalLine.review2, nextOrder++) : 0;
  const check3Order = check3Enumber ? getSlotOrder(approvalLine.review3, nextOrder++) : 0;
  const check4Order = check4Enumber ? getSlotOrder(approvalLine.review4, nextOrder++) : 0;

  const agreementOrder = agreementEnumber ? getSlotOrder(approvalLine.agreement1, nextOrder++) : 0;

  // 결재 상태(state) 계산 - PC 버전 로직과 동일
  // state 값 의미: 1=대기, 2=현재 차례(결재중/검토중), 3=완료, 6=수정요청
  // 기안자(sign1)는 상신 시 결재 완료 상태(3)
  const sign1State = 3;

  // 전역 순서 2 = 결재라인에서 "다음 결재 대상" (기안 다음 1번째 → 결재자 또는 검토자 중 순서상 첫 번째)
  const ORDER_FOR_NEXT_APPROVER = 2;

  // state 결정 규칙:
  // - 새상신(mode 0,1), 반려 후 재상신(mode 2 + docState 4): order 기준. order=2 한 명만 state=2 → 차례대로 진행.
  // - 결재중/수정요청 재상신(mode 2 + docState 2 또는 6): 기존 state 유지. 이미 결재한 3, 현재 차례 2, 대기 1 보존.
  function getSlotState(slotState, order, orderForNext = ORDER_FOR_NEXT_APPROVER) {
    if (docState === 6 && slotState === 6) return 6;
    if (slotState === 6) return 2;
    const preserveState = mode === 2 && (docState === 2 || docState === 6); // 결재중(2) 또는 수정요청(6) 재상신 시에만 유지
    if (preserveState) {
      return slotState ?? (order === orderForNext ? 2 : 1);
    }
    // 새상신, 임시상신, 반려 후 재상신: order가 2인 슬롯만 state=2
    return order === orderForNext ? 2 : 1;
  }

  // 결재자 상태 계산
  const sign2State = getSlotState(approvalLine.approval1?.state, sign2Order);
  const sign3State = getSlotState(approvalLine.approval2?.state, sign3Order);
  const sign4State = getSlotState(approvalLine.approval3?.state, sign4Order);
  const sign5State = getSlotState(approvalLine.approval4?.state, sign5Order);

  // 검토자 상태 계산
  const check1State = getSlotState(approvalLine.review1?.state, check1Order);
  const check2State = getSlotState(approvalLine.review2?.state, check2Order);
  const check3State = getSlotState(approvalLine.review3?.state, check3Order);
  const check4State = getSlotState(approvalLine.review4?.state, check4Order);

  // 합의자 상태 계산
  const agreementState = getSlotState(approvalLine.agreement1?.state, agreementOrder);

  return {
    sign1: sign1Enumber,
    sign2: sign2Enumber,
    sign3: sign3Enumber,
    sign4: sign4Enumber,
    sign5: sign5Enumber,
    check1: check1Enumber,
    check2: check2Enumber,
    check3: check3Enumber,
    check4: check4Enumber,
    agreement: agreementEnumber,
    sign1_order: sign1Order,
    sign2_order: sign2Order,
    sign3_order: sign3Order,
    sign4_order: sign4Order,
    sign5_order: sign5Order,
    check1_order: check1Order,
    check2_order: check2Order,
    check3_order: check3Order,
    check4_order: check4Order,
    agreement_order: agreementOrder,
    sign1_state: sign1State,
    sign2_state: sign2State,
    sign3_state: sign3State,
    sign4_state: sign4State,
    sign5_state: sign5State,
    check1_state: check1State,
    check2_state: check2State,
    check3_state: check3State,
    check4_state: check4State,
    agreement_state: agreementState,
  };
}

export default convertApprovalLine;
